import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildProviderDetailPath,
  PROVIDER_HEALTH_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { ProviderSummary, ProviderSummaryWindow } from '../domain/types';
import { formatCount, formatRelativeTime } from '../utils/format.utils';
import { getProviderHealth } from '../utils/provider-health.utils';
import { OutcomeStrip, OUTCOME_STRIP_UNAVAILABLE_LABEL } from './outcome-strip';
import { StatusChip } from './status-chip';
import { SurfaceCard } from './surface-card';

export const providerCardTestId = (dataProvider: string): string =>
  `pdg-provider-card-${dataProvider}`;
export const PROVIDER_CARD_LOAD_ERROR_TEXT = 'Could not load recent files.';
export const PROVIDER_CARD_NO_FILES_TEXT = 'No files yet';

/** "Last 10 files" or "Last 30 days", whichever the summary actually covers. */
export const describeWindow = (window: ProviderSummaryWindow): string =>
  window.kind === 'lastFiles'
    ? `Last ${window.size} files`
    : `Last ${window.days} days`;

interface ProviderCardProps {
  summary: ProviderSummary;
}

/** One provider at a glance. The whole card is the link to its detail page. */
export const ProviderCard = ({ summary }: ProviderCardProps): JSX.Element => {
  const basePath = useBasePath();
  const health = getProviderHealth(summary);
  const latest = summary.latestIngestion;
  const { completed, partial, rejected, failed } = summary.recentCounts;

  return (
    <SurfaceCard
      component={RouterLink}
      to={`${basePath}/${buildProviderDetailPath(summary.dataProvider)}`}
      isInteractive
      data-testid={providerCardTestId(summary.dataProvider)}
      paddingX={5}
      paddingY={4}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        opacity: summary.loadError ? 0.6 : 1,
      }}
    >
      <HHStack gap={3}>
        <HHStack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
        >
          <HHTypography
            hhVariant="variant-bypass"
            variant="body1"
            sx={{ fontWeight: 600, wordBreak: 'break-all' }}
          >
            {summary.dataProvider}
          </HHTypography>
          <StatusChip status={health} label={PROVIDER_HEALTH_LABELS[health]} />
        </HHStack>

        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
        >
          {summary.loadError
            ? PROVIDER_CARD_LOAD_ERROR_TEXT
            : latest
              ? `${latest.fileName} · ${formatRelativeTime(latest.createdAt)}`
              : PROVIDER_CARD_NO_FILES_TEXT}
        </HHTypography>

        <OutcomeStrip
          outcomes={summary.recentOutcomes}
          label={
            summary.loadError ? OUTCOME_STRIP_UNAVAILABLE_LABEL : undefined
          }
          testId={`${providerCardTestId(summary.dataProvider)}-strip`}
        />

        <HHStack direction="row" justifyContent="space-between" gap={2}>
          <HHTypography
            hhVariant="variant-bypass"
            variant="caption"
            color="text.secondary"
          >
            {`${describeWindow(summary.window)}: ${formatCount(completed + partial)} loaded, ${formatCount(rejected)} rejected, ${formatCount(failed)} failed`}
          </HHTypography>
          <HHTypography
            hhVariant="variant-bypass"
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {`${formatCount(summary.recentRowsParsed)} rows`}
          </HHTypography>
        </HHStack>
      </HHStack>
    </SurfaceCard>
  );
};
